/**
 * Cent-Arithmetik. Alle Beträge sind ganze Cent. Zwischenrechnungen mit Brüchen
 * laufen exakt über BigInt; gerundet wird kaufmännisch (ab 0,5 vom Nullpunkt weg).
 */
import { CURRENCIES, DomainError, type Cents, type Currency, type Finding, type MoneyLocale } from "./types.js";

/** Skala für Eingaben mit Bruchteilen von Cent (bis 4 Nachkommastellen je Cent). */
const SUB = 10_000n;

export function assertCents(value: number, field = "amount"): Cents {
  if (!Number.isSafeInteger(value)) throw new DomainError("NOT_CENTS", `${field}: ${value} ist kein ganzer Cent-Betrag`);
  return value;
}

/** Kaufmännische Division ganzer Zahlen: n/d, ab ,5 vom Nullpunkt weg. */
export function divRound(n: bigint, d: bigint): bigint {
  if (d === 0n) throw new DomainError("DIV_BY_ZERO");
  if (d < 0n) {
    n = -n;
    d = -d;
  }
  const neg = n < 0n;
  const a = neg ? -n : n;
  const q = a / d;
  const r = a % d;
  const res = r * 2n >= d ? q + 1n : q;
  return neg ? -res : res;
}

/** Bruchteil-Cent-Zahl (z. B. 3333.3) exakt in die interne Skala bringen. */
function toSub(value: number): bigint {
  if (!Number.isFinite(value)) throw new DomainError("NOT_FINITE");
  return BigInt(Math.round(value * Number(SUB)));
}

/** Prozentsatz (bis 4 Nachkommastellen) als ganze Zahl in 1/1_000_000. */
function percentToMicro(percent: number): bigint {
  if (!Number.isFinite(percent)) throw new DomainError("NOT_FINITE");
  return BigInt(Math.round(percent * 10_000)); // 1 % = 10_000
}
const PERCENT_SCALE = 1_000_000n; // 100 % = 1_000_000

/** Kaufmännisch auf ganze Cent runden (auch für Bruchteil-Cent-Eingaben). */
export function roundCents(value: number): Cents {
  return Number(divRound(toSub(value), SUB));
}

export function sumCents(values: readonly Cents[]): Cents {
  let s = 0;
  for (const v of values) s += assertCents(v);
  return assertCents(s);
}

/** Betrag × Prozent, kaufmännisch auf Cent gerundet. */
export function percentOf(amount: number, percent: number): Cents {
  return Number(divRound(toSub(amount) * percentToMicro(percent), SUB * PERCENT_SCALE));
}

/** Betrag × Zähler / Nenner, kaufmännisch gerundet (z. B. Kursverhältnisse). */
export function mulDiv(amount: Cents, numerator: number, denominator: number): Cents {
  assertCents(amount);
  return Number(divRound(BigInt(amount) * toSub(numerator), toSub(denominator)));
}

/**
 * Betrag exakt auf Anteile verteilen (KZ-17). Rest-Cents gehen der Reihe nach an die
 * Anteile mit dem größten Rundungsrest; die Summe stimmt immer.
 */
export function allocate(total: Cents, weights: readonly number[]): Cents[] {
  assertCents(total);
  if (weights.length === 0) return [];
  const w = weights.map((x) => {
    if (!(x >= 0)) throw new DomainError("NEGATIVE_WEIGHT");
    return toSub(x);
  });
  const sumW = w.reduce((a, b) => a + b, 0n);
  if (sumW === 0n) throw new DomainError("ZERO_WEIGHTS");
  const t = BigInt(total);
  const base = w.map((x) => (t * x) / sumW);
  const rest = w.map((x, i) => t * x - (base[i] ?? 0n) * sumW);
  let left = t - base.reduce((a, b) => a + b, 0n);
  // Den Cent bekommt der Anteil mit dem betragsmäßig größten Rest, auch bei negativen Beträgen (Gutschrift, Storno; R-01)
  const abs = (x: bigint) => (x < 0n ? -x : x);
  const order = rest.map((r, i) => ({ r: abs(r), i })).sort((a, b) => (a.r === b.r ? a.i - b.i : a.r > b.r ? -1 : 1));
  const out = base.slice();
  const step = left >= 0n ? 1n : -1n;
  for (let k = 0; left !== 0n; k = (k + 1) % order.length) {
    const idx = order[k]!.i;
    out[idx] = (out[idx] ?? 0n) + step;
    left -= step;
  }
  return out.map((x) => Number(x));
}

export function allocateEvenly(total: Cents, parts: number): Cents[] {
  return allocate(total, Array.from({ length: parts }, () => 1));
}

// ---------------------------------------------------------------------------
// Eingabe/Parsing
// ---------------------------------------------------------------------------

/**
 * Dezimaltext → Cent. Versteht „1.234,50“, „1,234.50“, „1234.5“, „1'234.50“, „-12“.
 * Das letzte Komma/Punkt mit 1–2 Folgeziffern gilt als Dezimaltrenner. Tausendertrenner (Punkt oder Komma) nur in echten
 * Dreiergruppen, überall dasselbe Zeichen und nicht das Dezimalzeichen (GB-5): „1.2.3“, „12..5“, „12,345,67“, „12.345,678“
 * und „1234.567“ sind unlesbar (INVALID_AMOUNT) statt still 12,30 / 12,50 / 12.345,67 / 12.345.678,00 / 1.234.567,00.
 * „1.234“ und „1,234“ bleiben 1.234,00 (Tausendertrenner, dokumentiert).
 */
export function parseAmountToCents(input: string | number): Cents {
  if (typeof input === "number") return roundCents(input * 100);
  // Runde Beträge im österreichischen und schweizerischen Stil: „2.000,-“, „2.000,--“, „2400.-“, „1'500.–“ (PRAXIS-R2-8);
  // „Fr.“ bzw. „SFr.“ als Währungszeichen
  let s = input.trim().replace(/[\s  '’]/g, "").replace(/^(-?)S?Fr\./i, "$1")
    .replace(/[€$£]|CHF|EUR|USD|GBP/gi, "").replace(/(\d)[.,][-–—]{1,2}$/, "$1");
  if (s === "") throw new DomainError("EMPTY_AMOUNT");
  const neg = s.startsWith("-");
  if (neg) s = s.slice(1);
  const m = /^(.*?)([.,])(\d{1,2})$/.exec(s);
  let intPart = s;
  let frac = "";
  let decimal: string | null = null;
  if (m) {
    intPart = m[1] ?? "";
    decimal = m[2] ?? null;
    frac = m[3] ?? "";
  }
  // GB-5: Tausendertrenner nur als echte Dreiergruppen („1.234.567“), ein einziges Zeichen, nie gleich dem Dezimalzeichen
  const groupSeps = intPart.match(/[.,]/g) ?? [];
  if (groupSeps.length > 0) {
    const sep = groupSeps[0]!;
    if (groupSeps.some((c) => c !== sep) || sep === decimal || !/^[1-9]\d{0,2}(?:[.,]\d{3})+$/.test(intPart)) {
      throw new DomainError("INVALID_AMOUNT", input);
    }
  }
  intPart = intPart.replace(/[.,]/g, "");
  if (!/^\d*$/.test(intPart) || !/^\d*$/.test(frac) || (intPart === "" && frac === "")) {
    throw new DomainError("INVALID_AMOUNT", input);
  }
  const cents = Number(intPart || "0") * 100 + Number(frac.padEnd(2, "0") || "0");
  return assertCents(neg ? -cents : cents);
}

// ---------------------------------------------------------------------------
// Formatierung
// ---------------------------------------------------------------------------

/**
 * Geldbetrag je Locale formatieren. Zwischen Betrag und Symbol steht ein geschütztes
 * Leerzeichen (U+00A0); de-CH nutzt den Apostroph als Tausendertrenner („CHF 1'234.50“).
 */
export function formatMoney(cents: Cents, currency: Currency, locale: MoneyLocale): string {
  assertCents(cents);
  const s = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
  let out = s.replace(/ /g, " ");
  // de-CH: Intl liefert „CHF-1'500.00“; üblich ist „CHF -1'500.00“
  if (locale === "de-CH") out = out.replace(/’/g, "'").replace(/^([^\d\s-]+)-/, "$1\u00A0-");
  return out;
}

/** Locale für die Anzeige aus Sprache und Land ableiten. */
export function moneyLocaleFor(language: "de" | "en", country: string | null): MoneyLocale {
  if (language === "en") return "en-GB";
  if (country === "AT") return "de-AT";
  if (country === "CH" || country === "LI") return "de-CH";
  return "de-DE";
}

// ---------------------------------------------------------------------------
// Währungen
// ---------------------------------------------------------------------------

export function isCurrency(value: unknown): value is Currency {
  return typeof value === "string" && (CURRENCIES as readonly string[]).includes(value);
}

/** ST-17: Währung gegen die Liste der Agentur prüfen (Befund, keine Sperre). */
export function checkCurrencyAllowed(currency: string, allowed: readonly Currency[]): Finding | null {
  if (isCurrency(currency) && allowed.includes(currency)) return null;
  return { code: "CURRENCY_NOT_ALLOWED", severity: "error", params: { currency } };
}

/**
 * KZ-14/MA-02: Umrechnung mit eingefrorenem Verhältnis. `fromTotal` und `toTotal` sind
 * derselbe Betrag in beiden Währungen zum Zeitpunkt des Einfrierens.
 */
export function convertWithFrozenRate(amount: Cents, frozen: { fromTotal: Cents; toTotal: Cents }): Cents {
  if (frozen.fromTotal === 0) throw new DomainError("FROZEN_RATE_ZERO");
  return Number(divRound(BigInt(amount) * BigInt(frozen.toTotal), BigInt(frozen.fromTotal)));
}

// ---------------------------------------------------------------------------
// Positionen → Netto/USt/Brutto (ST-18, ST-19, KZ-04, KZ-05)
// ---------------------------------------------------------------------------

export interface LineInput {
  description: string;
  quantity: number;
  /** Einzelpreis in Cent; Bruchteile (bis 4 Stellen) sind als Eingabe erlaubt */
  unitPriceCents: number;
  /** provisionsfreie Position (Auslage): geht 1:1 durch */
  commissionFree?: boolean;
}

export interface ComputedLine {
  index: number;
  description: string;
  quantity: number;
  /** Einzelpreis nach Provisionsabzug, auf Cent gerundet */
  unitPriceCents: Cents;
  lineTotalCents: Cents;
  commissionFree: boolean;
}

export interface InvoiceTotals {
  lines: ComputedLine[];
  /** ausgelassene Positionen (Menge oder Preis ≤ 0) */
  omitted: number[];
  netCents: Cents;
  vatCents: Cents;
  grossCents: Cents;
  /** gerundetes Netto VOR Provisionsabzug */
  rawNetCents: Cents;
  /** Agenturanteil = gerundetes Rohnetto − Netto */
  commissionCents: Cents;
}

/** ST-05: Steuersatz auf 0–100 begrenzen, Nicht-Zahl = 0. */
export function clampRatePercent(input: unknown): number {
  const n = typeof input === "number" ? input : Number(String(input ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

/**
 * Rechnungssumme exakt: jede Position zuerst auf Cent (Einzelpreis nach Provisionsabzug),
 * Positionssumme = gerundeter Einzelpreis × Menge, Netto = Summe, USt = Netto × Satz gerundet,
 * Brutto = Netto + USt. Positionen mit Menge ≤ 0 oder Preis ≤ 0 entfallen.
 */
export function computeInvoiceTotals(
  lines: readonly LineInput[],
  opts: { vatRatePercent: number; commissionPercent?: number },
): InvoiceTotals {
  const rate = clampRatePercent(opts.vatRatePercent);
  const commission = opts.commissionPercent ?? 0;
  if (!(commission >= 0 && commission <= 100)) throw new DomainError("INVALID_COMMISSION", String(commission));
  const keep = PERCENT_SCALE - percentToMicro(commission);
  const out: ComputedLine[] = [];
  const omitted: number[] = [];
  let rawSub = 0n;
  lines.forEach((l, i) => {
    // Nullpreise und Mengen ≤ 0 entfallen; negative Einzelpreise (Rabatt, Anzahlungsabzug, Gutschrift) sind erlaubt
    if (!(l.quantity > 0) || !Number.isFinite(l.unitPriceCents) || l.unitPriceCents === 0) {
      omitted.push(i);
      return;
    }
    const unitSub = toSub(l.unitPriceCents);
    const qtySub = toSub(l.quantity);
    rawSub += divRound(unitSub * qtySub, SUB);
    const unit = l.commissionFree
      ? Number(divRound(unitSub, SUB))
      : Number(divRound(unitSub * keep, SUB * PERCENT_SCALE));
    const lineTotal = Number(divRound(BigInt(unit) * qtySub, SUB));
    out.push({
      index: i,
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: unit,
      lineTotalCents: lineTotal,
      commissionFree: !!l.commissionFree,
    });
  });
  const net = sumCents(out.map((l) => l.lineTotalCents));
  const vat = percentOf(net, rate);
  const rawNet = Number(divRound(rawSub, SUB));
  return {
    lines: out,
    omitted,
    netCents: net,
    vatCents: vat,
    grossCents: net + vat,
    rawNetCents: rawNet,
    commissionCents: rawNet - net,
  };
}
